import type { LevelCatalog } from "@highmarch/app";
import { RARITY_IDS, SUPPLY_IDS } from "@highmarch/domain";
import { RewardKind, isRewardKind } from "./rewardHomes";

/**
 * What the player holds, flattened to one number per key, so a grant from anywhere shows up as a difference between
 * two snapshots (rewardFly.ts takes one after every save). A key is a reward kind (`gems`) or a kind and a variant
 * (`sigil:rare`, `supply:warBanner`, `gear:42:ammo:epic`, `pet:pup`); unique things (gear, pets, skins) hold 1.
 * Presentation only: it reads the state the app's rules already wrote, never decides anything.
 */
export type Holdings = ReadonlyMap<string, number>;

/** One kind of thing gained since the last snapshot. */
export interface Gain {
  readonly kind: RewardKind;
  /** The variant after the kind's `:` ("" for none): a rarity, a supply id, a pet id, `id:slot:rarity` for gear. */
  readonly variant: string;
  readonly amount: number;
  /** What the key holds now (the counter's value to tick up to). */
  readonly total: number;
}

const KEY_SEP = ":";
const key = (kind: RewardKind, variant?: string | number): string => (variant === undefined ? kind : `${kind}${KEY_SEP}${variant}`);

/** The holdings snapshot of `catalog` now. */
export function readHoldings(catalog: LevelCatalog): Holdings {
  const m = new Map<string, number>();
  const p = catalog.profile, w = catalog.economy.wallet, B = catalog.balance;
  m.set(key("gems"), w.gems);
  m.set(key("gold"), p.gold);
  m.set(key("marks"), w.marks);
  m.set(key("trophies"), w.trophies);
  m.set(key("essence"), p.forge.essence);
  p.forge.sigils.forEach((n, r) => m.set(key("sigil", RARITY_IDS[r]), n));
  for (const g of p.gear) m.set(key("gear", `${g.id}${KEY_SEP}${B.gear.slots[g.slot]?.id ?? ""}${KEY_SEP}${RARITY_IDS[g.rarity]}`), 1);
  m.set(key("speedUp"), p.speedUps.length);
  for (const id of SUPPLY_IDS) m.set(key("supply", id), p.supplies.count(id));
  for (const pet of p.pets.owned()) m.set(key("pet", pet.id), 1);
  for (const t of p.pets.data.treats) m.set(key("treat", t.id), p.pets.treatCount(t.id));
  for (const s of B.commerce.shop.skins.list) if (catalog.ownsSkin(s.id)) m.set(key("skin", s.id), 1);
  for (const e of catalog.endgame.events.emblems) m.set(key("emblem", e), 1);
  m.set(key("embers"), Object.values(catalog.endgame.events.toJson().embers).reduce((a, n) => a + n, 0));
  m.set(key("builder"), p.buildersOwned);
  m.set(key("vipXp"), catalog.deals.vipRecord.xp);
  return m;
}

/** What `after` holds more of than `before` (a spend, or something gone, is not a gain). */
export function diffHoldings(before: Holdings, after: Holdings): Gain[] {
  const out: Gain[] = [];
  for (const [k, total] of after) {
    const amount = total - (before.get(k) ?? 0);
    if (amount <= 0) continue;
    const cut = k.indexOf(KEY_SEP), kind = cut < 0 ? k : k.slice(0, cut);
    if (isRewardKind(kind)) out.push({ kind, variant: cut < 0 ? "" : k.slice(cut + 1), amount, total });
  }
  return out;
}

/** Gains grouped by kind, in REWARD_KINDS order of first appearance (gold before gear, as the grant lists them). */
export function groupGains(gains: readonly Gain[]): Map<RewardKind, Gain[]> {
  const g = new Map<RewardKind, Gain[]>();
  for (const x of gains) { const list = g.get(x.kind); if (list) list.push(x); else g.set(x.kind, [x]); }
  return g;
}

/** Two batches of gains merged (gains parked while the hub is away add up): same key → amounts summed, latest total. */
export function mergeGains(a: readonly Gain[], b: readonly Gain[]): Gain[] {
  const byKey = new Map<string, Gain>();
  for (const x of [...a, ...b]) {
    const k = x.variant === "" ? x.kind : `${x.kind}${KEY_SEP}${x.variant}`, prev = byKey.get(k);
    byKey.set(k, prev ? { ...x, amount: prev.amount + x.amount } : x);
  }
  return [...byKey.values()];
}
